import { useEffect, useMemo, useState } from "react";
import { PawPrint, Scale } from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import { Button, Field, SelectField, StatusBadge } from "@fawkes/ui";

import { formatEuro } from "../i18n/format";
import { ComboBox } from "./ComboBox";
import { RelationPicker } from "../components/RelationPicker";
import { ProductTariefPicker, type GekozenProductRegel } from "../components/ProductTariefPicker";
import { WizardModal, type WizardModalStep } from "./WizardModal";

/**
 * Nieuwe uitvaart aanmelden — dezelfde weg als inboeken bij het asiel, met dezelfde schermen.
 *
 * <p>De volgorde volgt hoe het werk binnenkomt: eerst wie het dier bracht (de relatie zoeken of
 * aanmaken, met de relatiekiezer van inboeken), dan het dier zelf, dan wat er met hem gebeurt en
 * wanneer, dan wat er verder meegaat (urn, sieraad), dan de stappen die het dossier gaat doorlopen,
 * en tot slot bevestigen.
 *
 * <p>De keuzelijsten halen hun waarden op waar de rest van het systeem ze ook haalt: de diersoorten,
 * rassen en kleuren uit de dierreferentie, de uitvaartvormen en hun handelingen uit de
 * crematoriuminstellingen, de producten uit de catalogus. Vrije tekst waar een lijst bestaat levert
 * gegevens op waar je later niet op kunt zoeken.
 *
 * <p>Wat er bij het bevestigen gebeurt: het dossier wordt aangemaakt (met de klant eraan en de
 * handelingen ingepland), daarna gaan de losse verkoopregels erop, en dan opent het dossier.
 */

const HERKOMSTEN = [
  "DOOR_EIGENAAR_GEBRACHT",
  "OPHALEN_BIJ_EIGENAAR",
  "VIA_DIERENARTS",
  "VIA_DERDE_PARTIJ",
] as const;

/** De vormen uit de tarieven, als terugval wanneer er nog geen uitvaartvormen zijn ingericht. */
const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

const STAPPEN = ["eigenaar", "dier", "crematie", "bijverkoop", "handelingen", "bevestigen"] as const;

interface Optie {
  id: number;
  label: string;
  speciesId?: number | null;
}

interface Uitvaartvorm {
  id: string;
  naam: string;
  verwerkingsvorm: string;
  aantalHandelingen: number;
}

interface Handeling {
  id: string;
  naam: string;
  vanafMoment: string | null;
  vanafHandelingId?: string | null;
  dagen: number | null;
  werkdagen: boolean;
  verplicht: boolean;
}

/** Een losse verkoopregel op de uitvaart: een urn, een sieraad, een aandenken. */
interface Verkoopregel {
  sleutel: string;
  omschrijving: string;
  aantal: string;
  prijs: string;
  btw: string;
}

/** Eén regel uit de kostenopzet; het soort zegt hoe het scherm hem benoemt. */
interface Kostenregel {
  soort: "TARIEF" | "TOESLAG" | "VERVOER";
  omschrijving: string;
  bedrag: number | null;
  btwPercentage: number | null;
  inclBtw: number | null;
}

interface PrijsIndicatie {
  gewichtsklasseId: string | null;
  gewichtsklasseNaam: string | null;
  gevonden: boolean;
  prijs: number | null;
  btwPercentage: number | null;
  inclBtw: number | null;
  /** Wat er al vastligt: het tarief, de toeslagen die vanzelf meegaan, het ophalen. */
  regels?: Kostenregel[];
  vastTotaalInclBtw?: number | null;
  /** Wat elke verwerkingsvorm kost voor deze klasse, ook voordat er een vorm gekozen is. */
  perVorm?: { verwerkingsvorm: string; prijs: number | null; btwPercentage: number | null; inclBtw: number | null }[];
}

interface WizardForm {
  dierNaam: string;
  diersoortId: string;
  diersoort: string;
  rasId: string;
  ras: string;
  geslacht: string;
  kleurId: string;
  kleur: string;
  gewichtKg: string;
  chipTatoeage: string;
  datumOverlijden: string;
  eigenaarNaam: string;
  eigenaarAdres: string;
  eigenaarPostcode: string;
  eigenaarPlaats: string;
  eigenaarTelefoon: string;
  eigenaarEmail: string;
  geenEmail: boolean;
  betalerNaam: string;
  herkomst: string;
  uitvaartvormId: string;
  verwerkingsvorm: string;
  uitvaartDatum: string;
  uitvaartTijd: string;
  spoed: string;
}

const LEEG: WizardForm = {
  dierNaam: "",
  diersoortId: "",
  diersoort: "",
  rasId: "",
  ras: "",
  geslacht: "",
  kleurId: "",
  kleur: "",
  gewichtKg: "",
  chipTatoeage: "",
  datumOverlijden: "",
  eigenaarNaam: "",
  eigenaarAdres: "",
  eigenaarPostcode: "",
  eigenaarPlaats: "",
  eigenaarTelefoon: "",
  eigenaarEmail: "",
  geenEmail: false,
  betalerNaam: "",
  herkomst: "",
  uitvaartvormId: "",
  verwerkingsvorm: "",
  uitvaartDatum: "",
  uitvaartTijd: "",
  spoed: "nee",
};

type WizardNotice = "forbidden" | "validatie" | "error";

/**
 * Vanaf een klantscherm draagt de aanmelding die klant mee: zijn gegevens staan al ingevuld en het
 * dossier komt aan hem te hangen. Zonder klant begint de wizard bij het zoeken van de relatie.
 */
export interface CrematieWizardKlant {
  relatieId: number;
  naam?: string | null;
  adres?: string | null;
  postcode?: string | null;
  plaats?: string | null;
  telefoon?: string | null;
  email?: string | null;
}

const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function useFeed<T>(url: string, enabled = true): T[] {
  const { data } = useCustom({ url, method: "get", queryOptions: { enabled } });
  const rijen = data?.data as T[] | { content?: T[] } | undefined;
  if (Array.isArray(rijen)) return rijen;
  return (rijen as { content?: T[] } | undefined)?.content ?? [];
}

export function CrematieAanmeldingWizard({
  onCancel,
  onCreated,
  klant,
  onExpand,
}: {
  onCancel?: () => void;
  /** Het nieuwe dossier, en de klant waar het aan hangt — zodat het scherm bij die klant opent. */
  onCreated: (dossierId: string, relatieId: number | null) => void;
  klant?: CrematieWizardKlant;
  /** Vanuit de dialoog: de wizard als volledige pagina openen. */
  onExpand?: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data: rechten } = usePermissions<string[]>();
  const magRelatieMaken = (rechten ?? []).includes("ROLE_RELATIE_EDIT");

  // Met een klant meegegeven begint de wizard bij het dier: de eigenaar is al bekend.
  const [step, setStep] = useState(klant ? 1 : 0);
  const [notice, setNotice] = useState<WizardNotice | null>(null);
  /** De redenen die de server teruggaf bij een 422 — preciezer dan de algemene melding. */
  const [redenen, setRedenen] = useState<string[]>([]);
  const [form, setForm] = useState<WizardForm>(() => (klant
    ? {
        ...LEEG,
        eigenaarNaam: klant.naam ?? "",
        eigenaarAdres: klant.adres ?? "",
        eigenaarPostcode: klant.postcode ?? "",
        eigenaarPlaats: klant.plaats ?? "",
        eigenaarTelefoon: klant.telefoon ?? "",
        eigenaarEmail: klant.email ?? "",
      }
    : LEEG));

  const set = (veld: keyof WizardForm) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [veld]: e.target.value }));

  // ── stap 1: de relatie ──
  const [relatieId, setRelatieId] = useState<number | null>(klant?.relatieId ?? null);
  const [relatieNaam, setRelatieNaam] = useState<string | null>(klant?.naam ?? null);

  // De gekozen relatie vult de eigenaarsgegevens; die staan op het dossier los van de relatie, zodat
  // een latere adreswijziging een afgeronde uitvaart niet met terugwerkende kracht verandert.
  const { data: relatieData } = useCustom({
    url: `intake/reference/klanten/${relatieId}`,
    method: "get",
    queryOptions: { enabled: relatieId != null },
  });
  useEffect(() => {
    const k = relatieData?.data as Record<string, unknown> | undefined;
    if (!k) return;
    const tekst = (sleutel: string) => (typeof k[sleutel] === "string" ? (k[sleutel] as string) : "");
    setForm((f) => ({
      ...f,
      eigenaarNaam: tekst("naam") || tekst("weergavenaam") || relatieNaam || f.eigenaarNaam,
      eigenaarAdres: [tekst("straat"), tekst("huisnummer") + tekst("huisnummerToevoeging")]
        .filter((deel) => deel.trim() !== "").join(" ") || f.eigenaarAdres,
      eigenaarPostcode: tekst("postcode") || f.eigenaarPostcode,
      eigenaarPlaats: tekst("woonplaats") || tekst("plaats") || f.eigenaarPlaats,
      eigenaarTelefoon: tekst("telefoon1") || tekst("telefoon") || f.eigenaarTelefoon,
      eigenaarEmail: tekst("emailAdres") || tekst("email") || f.eigenaarEmail,
    }));
  }, [relatieData, relatieNaam]);

  // ── stap 2: het dier, uit de referentielijsten ──
  const soorten = useFeed<Optie>("animals/reference/species");
  const rassen = useFeed<Optie>(
    `animals/reference/breeds${form.diersoortId ? `?species=${form.diersoortId}` : ""}`,
  );
  const kleuren = useFeed<Optie>("animals/reference/colours");

  // ── stap 3: de uitvaartvormen uit de instellingen ──
  const vormen = useFeed<Uitvaartvorm>("crematorium/uitvaartvormen");
  const gekozenVorm = vormen.find((v) => v.id === form.uitvaartvormId) ?? null;

  // ── stap 5: de handelingen van die vorm ──
  const handelingen = useFeed<Handeling>(
    `crematorium/uitvaartvormen/${form.uitvaartvormId}/handelingen`,
    !!form.uitvaartvormId,
  );

  // ── stap 4: de bijverkoop, via dezelfde productkiezer als inboeken ──
  const [kiezerOpen, setKiezerOpen] = useState(false);
  const [regels, setRegels] = useState<Verkoopregel[]>([]);
  /** De kiezer geeft een prijs inclusief btw; de regel rekent exclusief, zoals de factuur. */
  const voegProductToe = (gekozen: GekozenProductRegel) => {
    // Altijd gezet: de kiezer laat bij een product zonder BTW eerst een percentage kiezen.
    const btw = gekozen.btwPercentage;
    const excl = gekozen.prijsInclBtw / (1 + btw / 100);
    setRegels((r) => [...r, {
      sleutel: `${Date.now()}-${r.length}`,
      omschrijving: gekozen.omschrijving,
      aantal: String(gekozen.aantal),
      prijs: excl.toFixed(2),
      btw: String(btw),
    }]);
  };

  // ── stap 5: per handeling de dag en het tijdstip; het schema doet een voorstel ──
  const [planning, setPlanning] = useState<Record<string, { datum: string; tijd: string }>>({});
  const vandaag = new Date().toISOString().slice(0, 10);
  /**
   * Het voorstel per handeling, gerekend zoals de server het doet: zoveel dagen na het anker
   * (aanmelding = vandaag, uitvaart = de gekozen datum, ontvangst = nog onbekend), bij werkdagen
   * zonder het weekend. De balie kan er overheen typen.
   */
  const voorstel = useMemo(() => {
    const uit: Record<string, string> = {};
    for (const h of handelingen) {
      const basis = h.vanafHandelingId
        ? (uit[h.vanafHandelingId] || null)
        : h.vanafMoment === "AANMELDING" ? vandaag
        : h.vanafMoment === "UITVAART" ? (form.uitvaartDatum || null)
        : null;
      uit[h.id] = basis ? verschuif(basis, h.dagen ?? 0, h.werkdagen) : "";
    }
    return uit;
  }, [handelingen, form.uitvaartDatum, vandaag]);
  const geplandVoor = (id: string) => ({
    datum: planning[id]?.datum ?? voorstel[id] ?? "",
    tijd: planning[id]?.tijd ?? "",
  });
  const zetPlanning = (id: string, veld: "datum" | "tijd", waarde: string) =>
    setPlanning((pl) => ({ ...pl, [id]: { ...geplandVoor(id), [veld]: waarde } }));
  const zetRegel = (sleutel: string, veld: keyof Verkoopregel, waarde: string) =>
    setRegels((r) => r.map((x) => (x.sleutel === sleutel ? { ...x, [veld]: waarde } : x)));
  const regelTotaal = regels.reduce(
    (s, r) => s + (num(r.aantal) ?? 0) * (num(r.prijs) ?? 0) * (1 + (num(r.btw) ?? 0) / 100),
    0,
  );

  // ── de prijsindicatie: welke gewichtsklasse en wat kost die ──
  const gewicht = num(form.gewichtKg);
  const { data: indicatieData } = useCustom<PrijsIndicatie>({
    url: `crematorium/aanmeldingen/prijsindicatie?gewichtKg=${gewicht ?? ""}&verwerkingsvorm=${form.verwerkingsvorm}`
      + `&herkomst=${form.herkomst}&spoed=${form.spoed === "ja"}`,
    method: "get",
    queryOptions: { enabled: gewicht != null },
  });
  const indicatie = indicatieData?.data;

  const euro = (n: number | null | undefined) => (n == null ? "—" : formatEuro(n, locale));

  /** De opzet zoals de server hem rekent; leeg zolang er geen gewicht en vorm gekozen zijn. */
  const kostenregels = indicatie?.regels ?? [];
  const vastTotaal = indicatie?.vastTotaalInclBtw ?? 0;
  const totaal = vastTotaal + regelTotaal;

  // Precies de eisen die de server stelt aan een definitieve aanmelding. Een checklist die minder
  // vraagt dan de server staat op groen terwijl het opslaan afketst; dan zoek je in het verkeerde
  // scherm naar wat er mis is.
  const checks = useMemo(() => [
    { key: "dierNaam", ok: form.dierNaam.trim() !== "" },
    { key: "diersoort", ok: form.diersoort.trim() !== "" },
    { key: "gewicht", ok: gewicht != null },
    { key: "overlijden", ok: form.datumOverlijden !== "" },
    { key: "eigenaar", ok: form.eigenaarNaam.trim() !== "" },
    { key: "contact", ok: form.eigenaarTelefoon.trim() !== "" || form.eigenaarEmail.trim() !== "" },
    { key: "verwerkingsvorm", ok: form.verwerkingsvorm !== "" },
    { key: "herkomst", ok: form.herkomst !== "" },
  ], [form, gewicht]);
  const alleseCompleet = checks.every((c) => c.ok);

  const { mutate, isLoading: bezig } = useCustomMutation();
  const { mutate: voegRegelToe } = useCustomMutation();

  const payload = () => ({
    dierNaam: form.dierNaam || null,
    diersoort: form.diersoort || null,
    ras: form.ras || null,
    geslacht: form.geslacht || null,
    kleur: form.kleur || null,
    gewichtKg: gewicht,
    chipTatoeage: form.chipTatoeage || null,
    datumOverlijden: form.datumOverlijden || null,
    eigenaarNaam: form.eigenaarNaam || null,
    eigenaarAdres: form.eigenaarAdres || null,
    eigenaarPostcode: form.eigenaarPostcode || null,
    eigenaarPlaats: form.eigenaarPlaats || null,
    eigenaarTelefoon: form.eigenaarTelefoon || null,
    eigenaarEmail: form.eigenaarEmail || null,
    geenEmail: form.geenEmail,
    betalerNaam: form.betalerNaam || null,
    herkomst: form.herkomst || null,
    verwerkingsvorm: form.verwerkingsvorm || null,
    spoed: form.spoed === "ja",
    relatieId,
    uitvaartvormId: form.uitvaartvormId || null,
    uitvaartDatum: form.uitvaartDatum || null,
    handelingen: handelingen.map((h) => {
      const g = geplandVoor(h.id);
      return { handelingId: h.id, verwachtOp: g.datum || null, verwachtTijd: g.tijd || null };
    }),
  });

  /**
   * Aanmaken en openen. De verkoopregels gaan er na het aanmaken op — het dossier moet eerst bestaan
   * voordat er een regel aan kan hangen. Mislukt een regel, dan is de uitvaart er nog steeds; die
   * regel voeg je in het dossier zelf toe, wat beter is dan de hele aanmelding weggooien.
   */
  const submit = (definitief: boolean) => {
    setNotice(null);
    setRedenen([]);
    mutate(
      {
        url: `crematorium/aanmeldingen?definitief=${definitief}`,
        method: "post",
        values: payload(),
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const id = (res?.data as { dossierId?: string } | undefined)?.dossierId;
          if (!id) return;
          if (regels.length === 0) {
            onCreated(id, relatieId);
            return;
          }
          let open = regels.length;
          const klaar = () => { open -= 1; if (open === 0) onCreated(id, relatieId); };
          for (const r of regels) {
            voegRegelToe(
              {
                url: `crematorium/dossiers/${id}/regels`,
                method: "post",
                values: {
                  omschrijving: r.omschrijving,
                  aantal: num(r.aantal) ?? 1,
                  prijs: num(r.prijs) ?? 0,
                  kortingPct: 0,
                  btwPercentage: num(r.btw) ?? 21,
                },
                successNotification: false,
                errorNotification: false,
              },
              { onSuccess: klaar, onError: klaar },
            );
          }
        },
        onError: (err) => {
          const fout = err as { statusCode?: number; response?: { data?: { fouten?: string[] } } };
          setNotice(fout.statusCode === 403 ? "forbidden" : fout.statusCode === 422 ? "validatie" : "error");
          setRedenen(fout.response?.data?.fouten ?? []);
        },
      },
    );
  };

  const jaNee = [
    { value: "nee", label: t("crematieAanmelding.nee") },
    { value: "ja", label: t("crematieAanmelding.ja") },
  ];

  // De relatiekiezer draagt op zijn zoekschermen zijn eigen knoppen; de wizardvoet hoort er dan
  // niet bij. Zodra er een eigenaar gekozen is mag je door, welk deelscherm de kiezer ook toont —
  // de fase zegt hoe hij eruitziet, de gekozen relatie zegt of je verder kunt.
  const voetVerbergen = step === 0 && relatieId == null;

  // Hoe ver de gebruiker is geweest: alleen bereikte stappen mag je in de balk terugklikken.
  const [verst, setVerst] = useState(step);
  const ga = (naar: number) => {
    const doel = Math.max(0, Math.min(STAPPEN.length - 1, naar));
    setStep(doel);
    setVerst((v) => Math.max(v, doel));
  };

  /**
   * De stappen in de wizardchrome van inboeken: de balk bovenaan, per stap een vraag met de velden
   * eronder, en één voet. Geen samenvattingsbalken boven de stap — de balk zegt al waar je bent,
   * en teruglopen doe je daar; een stapel herhaalde regels duwt het werk alleen naar beneden.
   */
  const stappen: WizardModalStep[] = [
    {
      key: "eigenaar",
      phase: 0,
      title: t("crematieAanmelding.eigenaar.zoek"),
      // De relatiekiezer brengt zijn eigen kop en zijn eigen kaart mee.
      hasBodyTitle: true,
      hideActions: voetVerbergen,
      card: false,
      content: (
        <RelationPicker
          label={t("crematieAanmelding.eigenaar.zoek")}
          variant="step"
          stepTitle={t("crematieAanmelding.eigenaar.zoek")}
          stepInfo={t("crematieAanmelding.eigenaar.zoekInfo")}
          valueId={relatieId}
          valueLabel={relatieNaam}
          onChange={(id, label) => { setRelatieId(id); setRelatieNaam(label); }}
          onCreated={() => ga(1)}
          canCreate={magRelatieMaken}
          feedPath="intake/reference/klanten"
          t={t}
        />
      ),
    },
    {
      key: "dier",
      phase: 1,
      title: t("crematieAanmelding.dier.vraag"),
      subtitle: t("crematieAanmelding.dier.sub"),
      content: dierStap(),
    },
    {
      key: "crematie",
      phase: 2,
      title: t("crematieAanmelding.crematie.vraag"),
      subtitle: t("crematieAanmelding.crematie.sub"),
      content: crematieStap(),
    },
    {
      key: "bijverkoop",
      phase: 3,
      title: t("crematieAanmelding.kosten.vraag"),
      subtitle: t("crematieAanmelding.kosten.sub"),
      content: bijverkoopStap(),
    },
    {
      key: "handelingen",
      phase: 4,
      title: t("crematieAanmelding.handelingen.vraag"),
      subtitle: t("crematieAanmelding.handelingen.sub"),
      content: handelingenStap(),
    },
    {
      key: "bevestigen",
      phase: 5,
      title: t("crematieAanmelding.bevestigen.vraag"),
      subtitle: t("crematieAanmelding.bevestigen.sub"),
      content: bevestigenStap(),
    },
  ];

  const laatste = STAPPEN.length - 1;

  return (
    <div className="fawkes-inboeken crem-aanmelding">
      <WizardModal
        title={t("crematieAanmelding.heading")}
        subtitle={t("crematieAanmelding.subtitle")}
        phases={STAPPEN.map((s) => t(`crematieAanmelding.stap.${s}`))}
        steps={stappen}
        active={step}
        furthest={verst}
        onEdit={ga}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onConfirm={() => (step === laatste ? submit(true) : ga(step + 1))}
        onCancel={() => onCancel?.()}
        onExpand={onExpand}
        expandLabel={t("wizard.openAsPage")}
        confirmDisabled={step === laatste && !alleseCompleet}
        busy={bezig}
        confirmLabel={t("wizard.confirm")}
        finishLabel={t("crematieAanmelding.aanmaken")}
        backLabel={t("common.back")}
        cancelLabel={t("crematieAanmelding.annuleren")}
      />
      <ProductTariefPicker
        open={kiezerOpen}
        onClose={() => setKiezerOpen(false)}
        onPick={voegProductToe}
        t={t}
        locale={locale}
      />
    </div>
  );

  // ── de stapinhoud ──────────────────────────────────────────────────────────────────────────────

  /**
   * De vormen om uit te kiezen bij het dier, elk met zijn prijs voor de gevonden klasse. Zijn er
   * uitvaartvormen ingericht, dan die (met hun eigen naam); anders de verwerkingsvormen waar een
   * tarief voor bestaat.
   */
  function vormKeuzes() {
    const prijsVan = (verwerkingsvorm: string) =>
      indicatie?.perVorm?.find((p) => p.verwerkingsvorm === verwerkingsvorm)?.inclBtw ?? null;
    if (vormen.length > 0) {
      return vormen.map((vm) => ({
        sleutel: vm.id,
        naam: vm.naam,
        inclBtw: prijsVan(vm.verwerkingsvorm),
        gekozen: form.uitvaartvormId === vm.id,
        kies: () => setForm((f) => ({ ...f, uitvaartvormId: vm.id, verwerkingsvorm: vm.verwerkingsvorm })),
      }));
    }
    return (indicatie?.perVorm ?? []).map((pv) => ({
      sleutel: pv.verwerkingsvorm,
      naam: t(`crematoriumTarief.vorm.${pv.verwerkingsvorm}`),
      inclBtw: pv.inclBtw,
      gekozen: form.verwerkingsvorm === pv.verwerkingsvorm,
      kies: () => setForm((f) => ({ ...f, verwerkingsvorm: pv.verwerkingsvorm })),
    }));
  }

  /**
   * Het dier, in de vorm van het dierscherm bij asiel: het formulier in een paneel links, en rechts
   * wat er uit die gegevens volgt. Andere velden dan bij asiel — vacht, kruising en castratie doen
   * er voor een crematie niet toe, gewicht en overlijdensdatum des te meer.
   */
  function dierStap() {
    return (
      <div className="fawkes-om-modal__cols">
        <div className="fawkes-om-modal__panel">
          <div className="fawkes-om-modal__panel-head">
            <span className="fawkes-om-modal__panel-icon"><PawPrint size={18} aria-hidden="true" /></span>
            <h3 className="fawkes-om-modal__panel-title">{t("crematieAanmelding.dier.panel")}</h3>
          </div>
          <div className="fawkes-om-modal__form">
            <div className="fawkes-om-modal__form-row">
              <div className="fawkes-inboeken__field">
                <Field label={t("crematieAanmelding.dier.naam")} value={form.dierNaam} onChange={set("dierNaam")} />
                {form.dierNaam.trim() === "" && (
                  <span className="fawkes-inboeken__field-error">{t("crematieAanmelding.dier.naamVerplicht")}</span>
                )}
              </div>
              <Field label={t("crematieAanmelding.dier.chip")} value={form.chipTatoeage} onChange={set("chipTatoeage")} />
            </div>
            <div className="fawkes-om-modal__form-row">
              <div className="fawkes-inboeken__field">
                <ComboBox
                  label={t("crematieAanmelding.dier.diersoort")}
                  value={form.diersoortId}
                  onChange={(v) => setForm((f) => ({
                    ...f,
                    diersoortId: v,
                    diersoort: soorten.find((so) => String(so.id) === v)?.label ?? "",
                    // Een ras hoort bij een soort: bij een andere soort is de oude keuze niet meer geldig.
                    rasId: "",
                    ras: "",
                  }))}
                  options={soorten.map((so) => ({ value: String(so.id), label: so.label }))}
                  placeholder={t("crematieAanmelding.dier.kiezen")}
                  invalid={form.diersoort.trim() === ""}
                />
                {form.diersoort.trim() === "" && (
                  <span className="fawkes-inboeken__field-error">{t("crematieAanmelding.dier.soortVerplicht")}</span>
                )}
              </div>
              {/* Zoals bij asiel: het ras staat niet op slot zolang er geen soort is. Kies je eerst een
                  ras, dan volgt de diersoort daaruit — een labrador is nu eenmaal een hond. */}
              <ComboBox
                label={t("crematieAanmelding.dier.ras")}
                value={form.rasId}
                onChange={(v) => {
                  const ras = rassen.find((r) => String(r.id) === v);
                  const soortId = ras?.speciesId != null ? String(ras.speciesId) : "";
                  setForm((f) => ({
                    ...f,
                    rasId: v,
                    ras: ras?.label ?? "",
                    diersoortId: f.diersoortId || soortId,
                    diersoort: f.diersoort || (soorten.find((so) => String(so.id) === soortId)?.label ?? ""),
                  }));
                }}
                options={rassen.map((r) => ({ value: String(r.id), label: r.label }))}
                placeholder={t("crematieAanmelding.dier.kiezen")}
              />
            </div>
            <div className="fawkes-om-modal__form-row">
              <SelectField
                label={t("crematieAanmelding.dier.geslacht")}
                value={form.geslacht}
                onChange={set("geslacht")}
                options={[
                  { value: "", label: "—" },
                  { value: "Reu", label: t("crematieAanmelding.dier.reu") },
                  { value: "Teef", label: t("crematieAanmelding.dier.teef") },
                  { value: "Onbekend", label: t("crematieAanmelding.dier.onbekend") },
                ]}
              />
              <ComboBox
                label={t("crematieAanmelding.dier.kleur")}
                value={form.kleurId}
                onChange={(v) => setForm((f) => ({
                  ...f,
                  kleurId: v,
                  kleur: kleuren.find((k) => String(k.id) === v)?.label ?? "",
                }))}
                options={kleuren.map((k) => ({ value: String(k.id), label: k.label }))}
                placeholder={t("crematieAanmelding.dier.kiezen")}
              />
            </div>
            <div className="fawkes-om-modal__form-row">
              <div className="fawkes-inboeken__field">
                <Field
                  label={t("crematieAanmelding.dier.gewicht")}
                  type="number"
                  value={form.gewichtKg}
                  onChange={set("gewichtKg")}
                />
                {gewicht == null && (
                  <span className="fawkes-inboeken__field-error">{t("crematieAanmelding.dier.gewichtVerplicht")}</span>
                )}
              </div>
              <div className="fawkes-inboeken__field">
                <Field
                  label={t("crematieAanmelding.dier.overlijden")}
                  type="date"
                  value={form.datumOverlijden}
                  onChange={set("datumOverlijden")}
                />
                {form.datumOverlijden === "" && (
                  <span className="fawkes-inboeken__field-error">{t("crematieAanmelding.dier.overlijdenVerplicht")}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Rechts wat er uit het gewicht volgt: de klasse, en wat die klasse kost. */}
        <div className="fawkes-om-modal__panel">
          <div className="fawkes-om-modal__panel-head">
            <span className="fawkes-om-modal__panel-icon"><Scale size={18} aria-hidden="true" /></span>
            <h3 className="fawkes-om-modal__panel-title">{t("crematieAanmelding.dier.gevolg")}</h3>
          </div>
          {gewicht == null ? (
            <p className="fawkes-muted">{t("crematieAanmelding.dier.gevolgLeeg")}</p>
          ) : (
            <>
              <p className="fawkes-muted" data-testid="gewichtsklasse">
                {indicatie?.gewichtsklasseNaam
                  ? t("crematieAanmelding.prijs.klasse", { args: [indicatie.gewichtsklasseNaam] })
                  : t("crematieAanmelding.prijs.geenKlasse")}
              </p>
              {/* De uitvaartvormen naast elkaar, met wat elk kost voor deze klasse. Aantikken kiest
                  de vorm alvast; de volgende stap neemt hem over. */}
              {indicatie?.gewichtsklasseNaam && (
                <>
                  <p className="fawkes-muted">{t("crematieAanmelding.dier.vormen")}</p>
                  <ul className="crem-aanmelding__vormen" data-testid="vormprijzen">
                    {vormKeuzes().map((k) => (
                      <li key={k.sleutel}>
                        <button
                          type="button"
                          className={"crem-aanmelding__vorm" + (k.gekozen ? " crem-aanmelding__vorm--gekozen" : "")}
                          aria-pressed={k.gekozen}
                          onClick={k.kies}
                        >
                          <span>{k.naam}</span>
                          <strong>{k.inclBtw == null ? t("crematieAanmelding.dier.vormGeenTarief") : euro(k.inclBtw)}</strong>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  /** De crematie: welke vorm en wanneer. */
  function crematieStap() {
    return (
      <>
        <div className="crem-detail__grid">
          {vormen.length > 0 ? (
            <SelectField
              label={t("crematieAanmelding.crematie.uitvaartvorm")}
              value={form.uitvaartvormId}
              onChange={(e) => {
                const gekozen = vormen.find((v) => v.id === e.target.value);
                setForm((f) => ({
                  ...f,
                  uitvaartvormId: e.target.value,
                  // De vorm draagt zijn verwerkingsvorm; die bepaalt het tarief.
                  verwerkingsvorm: gekozen?.verwerkingsvorm ?? f.verwerkingsvorm,
                }));
              }}
              options={[
                { value: "", label: "—" },
                ...vormen.map((v) => ({ value: v.id, label: v.naam })),
              ]}
            />
          ) : (
            <SelectField
              label={t("crematieAanmelding.verwerking.vorm")}
              value={form.verwerkingsvorm}
              onChange={set("verwerkingsvorm")}
              options={[
                { value: "", label: "—" },
                ...VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) })),
              ]}
            />
          )}
          <Field
            label={t("crematieAanmelding.crematie.datum")}
            type="date"
            value={form.uitvaartDatum}
            onChange={set("uitvaartDatum")}
          />
          <Field
            label={t("crematieAanmelding.crematie.tijd")}
            type="time"
            value={form.uitvaartTijd}
            onChange={set("uitvaartTijd")}
          />
          <SelectField
            label={t("crematieAanmelding.verwerking.herkomst")}
            value={form.herkomst}
            onChange={set("herkomst")}
            options={[
              { value: "", label: "—" },
              ...HERKOMSTEN.map((h) => ({ value: h, label: t(`aanmeldingen.herkomst.${h}`) })),
            ]}
          />
          <SelectField
            label={t("crematieAanmelding.verwerking.spoed")}
            value={form.spoed}
            onChange={set("spoed")}
            options={jaNee}
          />
        </div>
        {indicatie && (
          <p className="fawkes-muted">
            {indicatie.gewichtsklasseNaam
              ? t("crematieAanmelding.prijs.klasse", { args: [indicatie.gewichtsklasseNaam] })
              : t("crematieAanmelding.prijs.geenKlasse")}
            {indicatie.gevonden ? " · " + euro(indicatie.inclBtw) : ""}
          </p>
        )}
      </>
    );
  }

  /**
   * De kostenopzet: eerst wat al vastligt — het crematietarief van deze gewichtsklasse, de toeslagen
   * die vanzelf op de factuur komen, en het starttarief van de ophaalrit — en daaronder wat je er
   * zelf bij verkoopt. De vaste regels komen uit de tarieven en zijn hier niet te wijzigen; ze staan
   * er zodat te zien is waar de prijs vandaan komt in plaats van dat er één getal uit de lucht valt.
   */
  function bijverkoopStap() {
    const vormNaam = gekozenVorm?.naam
      ?? (form.verwerkingsvorm ? t(`crematoriumTarief.vorm.${form.verwerkingsvorm}`) : "");
    const naamVan = (r: Kostenregel) => {
      if (r.soort === "TARIEF") {
        return t("crematieAanmelding.kosten.tarief", { args: [vormNaam, r.omschrijving] });
      }
      if (r.soort === "VERVOER") {
        // Spoed is een toeslag op de rit; dat de regel die bevat moet je kunnen zien.
        return t("crematieAanmelding.kosten.ophalen", { args: [r.omschrijving] })
          + (form.spoed === "ja" ? " · " + t("crematieAanmelding.kosten.spoed") : "");
      }
      return r.omschrijving;
    };
    return (
      <>
        <table className="fui-table crem-aanmelding__kosten">
          <thead>
            <tr>
              <th>{t("crematieAanmelding.bijverkoop.omschrijving")}</th>
              <th className="crem-aanmelding__getal">{t("crematieAanmelding.bijverkoop.aantal")}</th>
              <th className="crem-aanmelding__getal">{t("crematieAanmelding.bijverkoop.prijs")}</th>
              <th className="crem-aanmelding__getal">{t("crematieAanmelding.bijverkoop.btw")}</th>
              <th className="crem-aanmelding__getal">{t("crematieAanmelding.kosten.bedrag")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {kostenregels.map((r, i) => (
              <tr key={`${r.soort}-${i}`} className="crem-aanmelding__kosten-vast">
                <td>{naamVan(r)}</td>
                <td className="crem-aanmelding__getal">1</td>
                <td className="crem-aanmelding__getal">{euro(r.bedrag)}</td>
                <td className="crem-aanmelding__getal">{(r.btwPercentage ?? 0) + "%"}</td>
                <td className="crem-aanmelding__getal">{euro(r.inclBtw)}</td>
                <td className="fawkes-muted">{t("crematieAanmelding.kosten.uitTarief")}</td>
              </tr>
            ))}
            {regels.map((r) => (
              <tr key={r.sleutel}>
                <td>{r.omschrijving}</td>
                <td className="crem-aanmelding__getal">
                  <input
                    aria-label={`${t("crematieAanmelding.bijverkoop.aantal")} ${r.omschrijving}`}
                    value={r.aantal}
                    onChange={(e) => zetRegel(r.sleutel, "aantal", e.target.value)}
                  />
                </td>
                <td className="crem-aanmelding__getal">
                  <input
                    aria-label={`${t("crematieAanmelding.bijverkoop.prijs")} ${r.omschrijving}`}
                    value={r.prijs}
                    onChange={(e) => zetRegel(r.sleutel, "prijs", e.target.value)}
                  />
                </td>
                <td className="crem-aanmelding__getal">
                  <input
                    aria-label={`${t("crematieAanmelding.bijverkoop.btw")} ${r.omschrijving}`}
                    value={r.btw}
                    onChange={(e) => zetRegel(r.sleutel, "btw", e.target.value)}
                  />
                </td>
                <td className="crem-aanmelding__getal">
                  {euro((num(r.aantal) ?? 0) * (num(r.prijs) ?? 0) * (1 + (num(r.btw) ?? 0) / 100))}
                </td>
                <td>
                  <button
                    type="button"
                    className="fawkes-link"
                    onClick={() => setRegels((x) => x.filter((y) => y.sleutel !== r.sleutel))}
                  >
                    {t("common.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={4}>{t("crematieAanmelding.kosten.totaal")}</th>
              <td className="crem-aanmelding__getal"><strong>{euro(totaal)}</strong></td>
              <td />
            </tr>
          </tfoot>
        </table>
        {kostenregels.length === 0 && (
          <p className="fawkes-muted">{t("crematieAanmelding.kosten.geen")}</p>
        )}
        {/* Wel een klasse en een vorm, maar geen tarief ervoor: dat is een gat in de tarieven, geen
            gat in de aanmelding — dus benoemen, niet verzwijgen. */}
        {indicatie != null && !indicatie.gevonden && indicatie.gewichtsklasseNaam && form.verwerkingsvorm && (
          <p className="fawkes-error">
            {t("crematieAanmelding.kosten.geenTarief", { args: [indicatie.gewichtsklasseNaam, vormNaam] })}
          </p>
        )}
        {kostenregels.some((r) => r.soort === "VERVOER") && (
          <p className="fawkes-muted">{t("crematieAanmelding.kosten.ophalenInfo")}</p>
        )}
        <p className="crem-aanmelding__concept">
          <Button variant="secondary" onClick={() => setKiezerOpen(true)}>
            + {t("crematieAanmelding.kosten.productToevoegen")}
          </Button>
        </p>
      </>
    );
  }

  /**
   * De handelingen die op het dossier komen, elk met de dag en het tijdstip waarop ze moeten
   * gebeuren. Het schema vult een voorstel in; de balie past het aan waar de familie of het
   * rooster iets anders vraagt. Een stap waarvan het anker nog niet vaststaat (ontvangst) blijft
   * leeg tot iemand hem invult.
   */
  function handelingenStap() {
    return (
      <>
        {!form.uitvaartvormId && (
          <p className="fawkes-muted">
            {t(vormen.length > 0
              ? "crematieAanmelding.handelingen.geenVorm"
              : "crematieAanmelding.handelingen.geenVormen")}
          </p>
        )}
        {form.uitvaartvormId && handelingen.length === 0 && (
          <p className="fawkes-muted">{t("crematieAanmelding.handelingen.geen")}</p>
        )}
        {handelingen.length > 0 && (
          <ul className="crem-aanmelding__planning" data-testid="handelingen">
            {handelingen.map((h) => {
              const g = geplandVoor(h.id);
              return (
                <li key={h.id} className="crem-aanmelding__planning-rij">
                  <div className="crem-aanmelding__planning-naam">
                    <strong>{h.naam}</strong>
                    {h.verplicht && <StatusBadge tone="neutral">{t("crematieAanmelding.handelingen.verplicht")}</StatusBadge>}
                    {h.dagen != null && h.vanafMoment && (
                      <span className="fawkes-muted">
                        {t(`crematieAanmelding.handelingen.moment.${h.vanafMoment}`, { args: [String(h.dagen)] })}
                      </span>
                    )}
                  </div>
                  <Field
                    label={t("crematieAanmelding.handelingen.datum")}
                    type="date"
                    value={g.datum}
                    onChange={(e) => zetPlanning(h.id, "datum", e.target.value)}
                  />
                  <Field
                    label={t("crematieAanmelding.handelingen.tijd")}
                    type="time"
                    value={g.tijd}
                    onChange={(e) => zetPlanning(h.id, "tijd", e.target.value)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  /** Bevestigen: wat het kost, wat er nog ontbreekt, en het wegleggen als concept. */
  function bevestigenStap() {
    return (
      <>
        <div className="crem-aanmelding__prijs">
          <span>{t("crematieAanmelding.prijs.titel")}</span>
          <strong>
            {indicatie?.gevonden ? euro(totaal) : t("crematieAanmelding.prijs.geenTarief")}
          </strong>
          {indicatie?.gewichtsklasseNaam && (
            <StatusBadge tone="neutral">{indicatie.gewichtsklasseNaam}</StatusBadge>
          )}
        </div>
        <ul className="crem-aanmelding__checklist">
          {checks.map((c) => (
            <li
              key={c.key}
              className={c.ok ? "crem-aanmelding__check--ok" : "crem-aanmelding__check--todo"}
            >
              <span aria-hidden="true">{c.ok ? "✓" : "○"}</span>
              {t(`crematieAanmelding.check.${c.key}`)}
            </li>
          ))}
        </ul>
        {notice && (
          <>
            <p className="fawkes-error">{t(`crematieAanmelding.${notice}`)}</p>
            {redenen.length > 0 && (
              <ul className="crem-aanmelding__stappen" data-testid="aanmeld-fouten">
                {redenen.map((r) => <li key={r} className="fawkes-error">{r}</li>)}
              </ul>
            )}
          </>
        )}
        {/* Onafgemaakt wegleggen kan altijd; de voet houdt één hoofdknop, dus deze staat hier. */}
        <p className="crem-aanmelding__concept">
          <Button variant="secondary" onClick={() => submit(false)} disabled={bezig}>
            {t("crematieAanmelding.concept")}
          </Button>
        </p>
      </>
    );
  }
}

/** Telt dagen op bij een ISO-datum; bij werkdagen worden zaterdag en zondag overgeslagen — als de server. */
function verschuif(basis: string, dagen: number, werkdagen: boolean): string {
  const d = new Date(basis + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  let over = Math.max(0, dagen);
  if (!werkdagen) {
    d.setDate(d.getDate() + over);
  } else {
    while (over > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) over -= 1;
    }
  }
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
